import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

const translations = {
  en: {
    // Navigation
    nav_home: 'Home',
    nav_products: 'Products',
    nav_services: 'Services',
    nav_how_it_works: 'How It Works',
    nav_for_suppliers: 'For Suppliers',
    nav_for_buyers: 'For Buyers',
    nav_contact: 'Contact',
    nav_login: 'Login',
    nav_register: 'Register',
    nav_dashboard: 'Dashboard',
    nav_logout: 'Logout',
    
    // Hero — Brand Strategy
    hero_title: 'Need something from South Africa?',
    hero_title_accent: "We're already there.",
    hero_subtitle: "Tell us what you need. We'll tell you the price, the timeline, and how we get it to you. No surprises.",
    hero_cta: 'Get a Quote',
    hero_cta_secondary: 'Browse Products',
    hero_tagline: 'Your guy on the other side.',
    
    // How It Works
    how_it_works_title: 'How It Works',
    how_it_works_subtitle: 'How It Works',
    how_step_1_title: 'Tell Us What You Need',
    how_step_1_desc: 'Browse our catalog or describe what you need. We source it from verified SA suppliers.',
    how_step_2_title: 'We Handle the Rest',
    how_step_2_desc: 'Pricing, logistics, customs, compliance. One partner, total visibility.',
    how_step_3_title: 'Confirm & Pay',
    how_step_3_desc: 'Review your quote, confirm payment. Track every step in real time.',
    how_step_4_title: 'Delivered to Your Door',
    how_step_4_desc: 'From Joburg to Maputo. We own the outcome, start to finish.',
    
    // Categories
    categories_title: 'What Are You Looking For?',
    categories_subtitle: 'From industrial equipment to electronics — if South Africa makes it, we move it.',
    categories_view_all: 'View All Products',
    
    // Brand Pillars (Value Props)
    value_props_title: 'Why businesses trust us.',
    value_props_subtitle: "We're not a freight forwarder. Not a courier. Not a sourcing agent. We're all three — and the layer above all three.",
    value_radical_clarity: 'Radical Clarity',
    value_radical_clarity_desc: 'No jargon. No confusion. Every step visible, trackable, and explained in plain language — in your language.',
    value_end_to_end: 'End-to-End Ownership',
    value_end_to_end_desc: "We don't hand off problems. We source it, move it, clear it, deliver it. The buck stops with us.",
    value_african_speed: 'African Speed',
    value_african_speed_desc: 'The Africa of hustle, improvisation, and getting it done when others say it can\'t be.',
    value_trust_infra: 'Trust as Infrastructure',
    value_trust_infra_desc: 'Verified suppliers, transparent pricing, documented every step. Trust isn\'t a promise — it\'s our product.',
    
    // CTA Section
    cta_title: 'Stop figuring out the border.',
    cta_subtitle: 'Start building your business. One call. Both sides handled.',
    cta_buyer: "I'm a Buyer",
    cta_supplier: "I'm a Supplier",
    
    // Trust quote
    trust_quote: "We know what it feels like to wire money to someone you've never met and hope for the best. That's exactly why we built this the way we did.",
    
    // Stats
    stats_suppliers: 'Verified Suppliers',
    stats_deliveries: 'Deliveries Completed',
    stats_countries: 'Countries Connected',
    stats_uptime: 'Uptime & Tracking',
    
    // Footer
    footer_tagline: 'Your guy on the other side.',
    footer_brand_desc: 'Cross-border procurement and logistics. Johannesburg to Maputo.',
    footer_company: 'Company',
    footer_about: 'About Us',
    footer_services: 'Services',
    footer_contact: 'Contact',
    footer_legal: 'Legal',
    footer_privacy: 'Privacy Policy',
    footer_terms: 'Terms of Service',
    footer_offices: 'Our Offices',
    footer_sa_office: 'Johannesburg, SA',
    footer_mz_office: 'Maputo, MZ',
    footer_rights: 'All rights reserved.',
    
    // Products
    products_title: 'Products',
    products_search: 'Search products...',
    products_filter: 'Filter',
    products_sort: 'Sort by',
    products_price_low: 'Price: Low to High',
    products_price_high: 'Price: High to Low',
    products_newest: 'Newest First',
    products_no_results: 'No products found',
    products_quote_only: 'Request Quote',
    products_add_quote: 'Add to Quote',
    products_view_details: 'View Details',
    products_min_qty: 'Min. Qty',
    
    // Auth
    auth_login_title: 'Welcome back.',
    auth_login_subtitle: 'Sign in to your account',
    auth_register_title: 'Join the network.',
    auth_register_subtitle: 'Create your account on The Guy in SA',
    auth_email: 'Email',
    auth_pwd: 'Password',
    auth_name: 'Full Name',
    auth_phone: 'Phone Number',
    auth_company: 'Company Name',
    auth_country: 'Country',
    auth_address: 'Address',
    auth_role: 'I am a',
    auth_buyer: 'Buyer',
    auth_supplier: 'Supplier',
    auth_login_btn: 'Sign In',
    auth_register_btn: 'Create Account',
    auth_google: 'Continue with Google',
    auth_no_account: "Don't have an account?",
    auth_has_account: 'Already have an account?',
    auth_forgot_pwd: 'Forgot password?',
    
    // Dashboard
    dashboard_overview: 'Overview',
    dashboard_orders: 'Orders',
    dashboard_quotes: 'Quotes',
    dashboard_products: 'Products',
    dashboard_settings: 'Settings',
    
    // Contact
    contact_title: 'Let\'s talk.',
    contact_subtitle: 'Have questions? We answer fast. In your language.',
    contact_name: 'Your Name',
    contact_email: 'Email Address',
    contact_phone: 'Phone Number',
    contact_subject: 'Subject',
    contact_message: 'Message',
    contact_send: 'Send Message',
    contact_whatsapp: 'Chat on WhatsApp',
    
    // Trending
    trending: 'Trending',
    featured_products: 'Popular Right Now',
    view_all: 'View All',
    
    // Common
    common_loading: 'Loading...',
    common_error: 'An error occurred',
    common_success: 'Success!',
    common_cancel: 'Cancel',
    common_save: 'Save',
    common_delete: 'Delete',
    common_edit: 'Edit',
    common_view: 'View',
    common_back: 'Back',
    common_next: 'Next',
    common_submit: 'Submit',
    common_zar: 'ZAR',
    common_mzn: 'MZN',
  },
  pt: {
    // Navigation
    nav_home: 'Inicio',
    nav_products: 'Produtos',
    nav_services: 'Servicos',
    nav_how_it_works: 'Como Funciona',
    nav_for_suppliers: 'Para Fornecedores',
    nav_for_buyers: 'Para Compradores',
    nav_contact: 'Contacto',
    nav_login: 'Entrar',
    nav_register: 'Registar',
    nav_dashboard: 'Painel',
    nav_logout: 'Sair',
    
    // Hero — Brand Strategy
    hero_title: 'Precisa de algo da Africa do Sul?',
    hero_title_accent: 'Nos ja estamos la.',
    hero_subtitle: 'Diga-nos o que precisa. Dizemos o preco, o prazo e como entregamos. Sem surpresas.',
    hero_cta: 'Pedir Cotacao',
    hero_cta_secondary: 'Ver Produtos',
    hero_tagline: 'O seu contacto do outro lado.',
    
    // How It Works
    how_it_works_title: 'Como Funciona',
    how_it_works_subtitle: 'Como Funciona',
    how_step_1_title: 'Diga-nos o Que Precisa',
    how_step_1_desc: 'Navegue o catalogo ou descreva o que precisa. Nos encontramos junto de fornecedores SA verificados.',
    how_step_2_title: 'Nos Tratamos do Resto',
    how_step_2_desc: 'Precos, logistica, alfandega, conformidade. Um parceiro, visibilidade total.',
    how_step_3_title: 'Confirme e Pague',
    how_step_3_desc: 'Revise a cotacao, confirme o pagamento. Acompanhe cada passo em tempo real.',
    how_step_4_title: 'Entregue a Sua Porta',
    how_step_4_desc: 'De Joburg a Maputo. Nos somos donos do resultado, do inicio ao fim.',
    
    // Categories
    categories_title: 'O Que Procura?',
    categories_subtitle: 'De equipamento industrial a electronica — se a Africa do Sul produz, nos movemos.',
    categories_view_all: 'Ver Todos os Produtos',
    
    // Brand Pillars (Value Props)
    value_props_title: 'Porque as empresas confiam em nos.',
    value_props_subtitle: 'Nao somos transitario. Nem estafeta. Nem agente de sourcing. Somos os tres — e a camada acima dos tres.',
    value_radical_clarity: 'Clareza Radical',
    value_radical_clarity_desc: 'Sem jargao. Sem confusao. Cada passo visivel, rastreavel e explicado em linguagem simples — na sua lingua.',
    value_end_to_end: 'Responsabilidade Total',
    value_end_to_end_desc: 'Nao passamos problemas. Encontramos, movemos, desalfandegamos, entregamos. A responsabilidade e nossa.',
    value_african_speed: 'Velocidade Africana',
    value_african_speed_desc: 'A Africa da luta, da improvisacao e de fazer acontecer quando outros dizem que nao da.',
    value_trust_infra: 'Confianca como Infraestrutura',
    value_trust_infra_desc: 'Fornecedores verificados, precos transparentes, tudo documentado. Confianca nao e promessa — e o nosso produto.',
    
    // CTA Section
    cta_title: 'Pare de decifrar a fronteira.',
    cta_subtitle: 'Comece a construir o seu negocio. Uma chamada. Os dois lados tratados.',
    cta_buyer: 'Sou Comprador',
    cta_supplier: 'Sou Fornecedor',
    
    // Trust quote
    trust_quote: 'Nos sabemos o que e transferir dinheiro para alguem que nunca conheceu e esperar pelo melhor. E exatamente por isso que construimos isto assim.',
    
    // Stats
    stats_suppliers: 'Fornecedores Verificados',
    stats_deliveries: 'Entregas Completas',
    stats_countries: 'Paises Conectados',
    stats_uptime: 'Uptime e Tracking',
    
    // Footer
    footer_tagline: 'O seu contacto do outro lado.',
    footer_brand_desc: 'Procurement e logistica transfronteirica. Joanesburgo a Maputo.',
    footer_company: 'Empresa',
    footer_about: 'Sobre Nos',
    footer_services: 'Servicos',
    footer_contact: 'Contacto',
    footer_legal: 'Legal',
    footer_privacy: 'Politica de Privacidade',
    footer_terms: 'Termos de Servico',
    footer_offices: 'Escritorios',
    footer_sa_office: 'Joanesburgo, SA',
    footer_mz_office: 'Maputo, MZ',
    footer_rights: 'Todos os direitos reservados.',
    
    // Products
    products_title: 'Produtos',
    products_search: 'Pesquisar produtos...',
    products_filter: 'Filtrar',
    products_sort: 'Ordenar por',
    products_price_low: 'Preco: Menor para Maior',
    products_price_high: 'Preco: Maior para Menor',
    products_newest: 'Mais Recentes',
    products_no_results: 'Nenhum produto encontrado',
    products_quote_only: 'Pedir Cotacao',
    products_add_quote: 'Adicionar a Cotacao',
    products_view_details: 'Ver Detalhes',
    products_min_qty: 'Qtd. Min',
    
    // Auth
    auth_login_title: 'Bem-vindo de volta.',
    auth_login_subtitle: 'Entre na sua conta',
    auth_register_title: 'Junte-se a rede.',
    auth_register_subtitle: 'Crie a sua conta no The Guy in SA',
    auth_email: 'Email',
    auth_pwd: 'Palavra-passe',
    auth_name: 'Nome Completo',
    auth_phone: 'Numero de Telefone',
    auth_company: 'Nome da Empresa',
    auth_country: 'Pais',
    auth_address: 'Morada',
    auth_role: 'Eu sou',
    auth_buyer: 'Comprador',
    auth_supplier: 'Fornecedor',
    auth_login_btn: 'Entrar',
    auth_register_btn: 'Criar Conta',
    auth_google: 'Continuar com Google',
    auth_no_account: 'Nao tem conta?',
    auth_has_account: 'Ja tem conta?',
    auth_forgot_pwd: 'Esqueceu a senha?',
    
    // Dashboard
    dashboard_overview: 'Visao Geral',
    dashboard_orders: 'Encomendas',
    dashboard_quotes: 'Cotacoes',
    dashboard_products: 'Produtos',
    dashboard_settings: 'Configuracoes',
    
    // Contact
    contact_title: 'Vamos conversar.',
    contact_subtitle: 'Tem perguntas? Respondemos rapido. Na sua lingua.',
    contact_name: 'O Seu Nome',
    contact_email: 'Endereco de Email',
    contact_phone: 'Numero de Telefone',
    contact_subject: 'Assunto',
    contact_message: 'Mensagem',
    contact_send: 'Enviar Mensagem',
    contact_whatsapp: 'Conversar no WhatsApp',
    
    // Trending
    trending: 'Tendencia',
    featured_products: 'Populares Agora',
    view_all: 'Ver Todos',
    
    // Common
    common_loading: 'A carregar...',
    common_error: 'Ocorreu um erro',
    common_success: 'Sucesso!',
    common_cancel: 'Cancelar',
    common_save: 'Guardar',
    common_delete: 'Eliminar',
    common_edit: 'Editar',
    common_view: 'Ver',
    common_back: 'Voltar',
    common_next: 'Seguinte',
    common_submit: 'Submeter',
    common_zar: 'ZAR',
    common_mzn: 'MZN',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'pt' : 'en');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isEnglish: language === 'en',
    isPortuguese: language === 'pt',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
